import { getBackendUrl, resolveAssetUrl } from '../lib/api';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PersonalInfoSection from '../components/apply/PersonalInfoSection';
import DocumentsSection from '../components/apply/DocumentsSection';
import { toast } from 'sonner';
import { CheckCircle, Loader2, ArrowLeft, ArrowRight, ClipboardList, Mail } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import { trackFunnel } from '../lib/funnel';

// Tiny inline beacon component — fires once on mount.
const FunnelBeacon = ({ event, source }) => {
  useEffect(() => {
    trackFunnel(event, { source });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

const ApplyPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [qualificationLink, setQualificationLink] = useState(null);
  const [countries, setCountries] = useState([]);
  const [roles, setRoles] = useState([]);
  const [logoUrl, setLogoUrl] = useState(null);
  const [platformName, setPlatformName] = useState('Talent Partner');
  const [speaksFrench, setSpeaksFrench] = useState(null);  // null = not answered, true = yes, false = no
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    job_categories: [],  // Changed: multi-select array of category IDs
    other_category_label: '',  // Free text when "Autre" is selected
    main_profession: '',
    secondary_skills: '',
    years_of_experience: 0,
    availability: 'full-time',
    contract_type: 'full', // 'full' = temps complet, 'partial' = temps partiel
    partial_percentage: null, // 30, 50, or 70 (only if contract_type is 'partial')
    french_level: 'fluent',
    has_computer: true,
    has_internet: true,
    remote_compatible: null,  // null = not answered, true/false after explicit choice
    portfolio_url: '',
    linkedin_profile: '',
    instagram_profile: '',
    facebook_profile: '',
    referral_source: '',
    motivation_text: '',
    expected_salary_local: '',
    currency: '',
    cv_file: null,
    portfolio_file: null,
    consent_data_accuracy: false,
    consent_data_storage: false,
    consent_data_sharing: false,
  });
  const [jobCategories, setJobCategories] = useState([]);
  const [jobCategoryGroups, setJobCategoryGroups] = useState([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const categoriesRef = useRef(null);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [salaryRange, setSalaryRange] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [expandedGroup, setExpandedGroup] = useState(null);  // null = aucun groupe ouvert (vue carte)

  // Clear a single field error when the user starts fixing it
  useEffect(() => {
    if (!hasAttemptedSubmit || Object.keys(fieldErrors).length === 0) return;
    const updated = { ...fieldErrors };
    let changed = false;
    // Clear simple text fields when they have a value
    ['first_name', 'last_name', 'phone', 'country', 'city', 'referral_source', 'other_category_label'].forEach((k) => {
      if (updated[k] && (formData[k] || '').toString().trim().length > 0) {
        delete updated[k];
        changed = true;
      }
    });
    // Special handling for email: only clear if email is valid (not just non-empty)
    if (updated.email) {
      const emailVal = (formData.email || '').trim();
      const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal);
      if (emailVal.length > 0 && emailIsValid) {
        delete updated.email;
        changed = true;
      }
    }
    if (updated.expected_salary_local && parseFloat(formData.expected_salary_local) > 0) {
      delete updated.expected_salary_local;
      changed = true;
    }
    if (updated.job_categories && (formData.job_categories || []).length > 0) {
      delete updated.job_categories;
      changed = true;
    }
    if (updated.cv_file && formData.cv_file) {
      delete updated.cv_file;
      changed = true;
    }
    ['consent_data_accuracy', 'consent_data_storage', 'consent_data_sharing'].forEach((k) => {
      if (updated[k] && formData[k]) {
        delete updated[k];
        changed = true;
      }
    });
    if (updated.currency && (formData.currency || '').trim().length > 0) {
      delete updated.currency;
      changed = true;
    }
    if (changed) setFieldErrors(updated);
  }, [formData, hasAttemptedSubmit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch salary range when country / main category / experience change
  useEffect(() => {
    const firstCat = (formData.job_categories && formData.job_categories[0]) || '';
    if (!formData.country || !firstCat) {
      setSalaryRange(null);
      return;
    }
    const params = new URLSearchParams({
      country: formData.country,
      job_category: firstCat,
      years_experience: String(formData.years_of_experience || 0),
    });
    const ctrl = new AbortController();
    fetch(`${getBackendUrl()}/api/public/salary-range?${params}`, { signal: ctrl.signal })
      .then(res => res.ok ? res.json() : null)
      .then(data => setSalaryRange(data))
      .catch(() => { /* silent */ });
    return () => ctrl.abort();
  }, [formData.country, formData.job_categories, formData.years_of_experience]);

  // Effective market range — proportional to the contract percentage.
  // If "Temps partiel" + X% is selected, the market range is scaled to X%
  // so the candidate's expectation stays coherent with the actual contract.
  const effectiveSalaryRange = (() => {
    if (!salaryRange?.min || !salaryRange?.max) return null;
    const isPartTime = formData.contract_type === 'partial';
    const pct = isPartTime && formData.partial_percentage
      ? Math.max(5, Math.min(100, Number(formData.partial_percentage)))
      : 100;
    const factor = pct / 100;
    return {
      ...salaryRange,
      min: Math.round(salaryRange.min * factor),
      max: Math.round(salaryRange.max * factor),
      isPartTime,
      percentage: pct,
    };
  })();

  // Compute live warning about salary vs market range.
  // NEVER blocks submission — this is purely informative to help the
  // candidate self-assess. The admin will judge the final fit.
  const salaryWarning = (() => {
    const local = parseFloat(formData.expected_salary_local);
    if (!local || !effectiveSalaryRange?.min || !effectiveSalaryRange?.max) return null;
    const { min, max, currency, isPartTime, percentage } = effectiveSalaryRange;
    const suffix = isPartTime ? ` (fourchette adaptée à temps partiel ${percentage}%)` : '';
    // Extreme zone (likely a typo) → visually red, but still NOT blocking
    if (local < min * 0.3) {
      return {
        level: 'extreme-low',
        text: `Ce montant semble très en-dessous du marché local (${min.toLocaleString('fr-FR')}—${max.toLocaleString('fr-FR')} ${currency || ''}${suffix}). Vérifiez qu'il n'y a pas un zéro manquant — sinon continuez, l'équipe examinera votre candidature.`,
      };
    }
    if (local > max * 3) {
      return {
        level: 'extreme-high',
        text: `Ce montant semble très au-dessus du marché local (${min.toLocaleString('fr-FR')}—${max.toLocaleString('fr-FR')} ${currency || ''}${suffix}). Vérifiez qu'il n'y a pas un zéro en trop — sinon continuez, l'équipe examinera votre candidature.`,
      };
    }
    // Outside ±30% tolerance → soft informative warning
    if (local < min * 0.7 || local > max * 1.3) {
      return {
        level: 'soft',
        text: `Votre montant sort de la fourchette marché (${min.toLocaleString('fr-FR')}—${max.toLocaleString('fr-FR')} ${currency || ''}, tolérance ±30%${suffix}). Vous pouvez tout de même postuler.`,
      };
    }
    return null;
  })();

  // Fallback categories if API fails (synced with backend taxonomy)
  const FALLBACK_CATEGORIES = [
    { id: 'community_manager', label_fr: 'Community Manager', group: 'marketing' },
    { id: 'media_buyer', label_fr: 'Media Buyer (Ads)', group: 'marketing' },
    { id: 'seo', label_fr: 'SEO / Référencement', group: 'marketing' },
    { id: 'email_marketing', label_fr: 'Email Marketing / CRM', group: 'marketing' },
    { id: 'lead_generation', label_fr: 'Lead Generation / Setter', group: 'marketing' },
    { id: 'commercial_b2b', label_fr: 'Commercial B2B', group: 'vente' },
    { id: 'closer', label_fr: 'Closer (high-ticket)', group: 'vente' },
    { id: 'graphiste', label_fr: 'Graphiste / Designer', group: 'contenu' },
    { id: 'ux_ui_designer', label_fr: 'UX/UI Designer', group: 'contenu' },
    { id: 'monteur_video', label_fr: 'Monteur Vidéo', group: 'contenu' },
    { id: 'copywriter', label_fr: 'Copywriter / Rédacteur', group: 'contenu' },
    { id: 'developpeur_web', label_fr: 'Développeur Web', group: 'tech' },
    { id: 'ai_automation', label_fr: 'AI Automation Specialist', group: 'tech' },
    { id: 'no_code_builder', label_fr: 'No-code Builder', group: 'tech' },
    { id: 'funnel_builder', label_fr: 'Funnel Builder', group: 'tech' },
    { id: 'assistant_virtuel', label_fr: 'Assistant(e) Virtuel(le)', group: 'ops' },
    { id: 'support_client', label_fr: 'Support Client', group: 'ops' },
    { id: 'comptabilite', label_fr: 'Comptabilité / Gestion', group: 'ops' },
    { id: 'recruteur_rh', label_fr: 'Recruteur / RH', group: 'ops' },
    { id: 'chef_de_projet', label_fr: 'Chef de projet', group: 'ops' },
    { id: 'autre', label_fr: 'Autre', group: 'autre' },
  ];

  const FALLBACK_GROUPS = [
    { id: 'marketing', label_fr: 'Marketing & Acquisition', order: 1 },
    { id: 'vente', label_fr: 'Vente', order: 2 },
    { id: 'contenu', label_fr: 'Contenu', order: 3 },
    { id: 'tech', label_fr: 'Tech & Automation', order: 4 },
    { id: 'ops', label_fr: 'Support & Ops', order: 5 },
    { id: 'autre', label_fr: 'Autre', order: 99 },
  ];

  useEffect(() => {
    // Fetch countries and roles
    fetch(getBackendUrl() + '/api/countries')
      .then(res => res.json())
      .then(data => setCountries(data.countries || []))
      .catch(err => console.error('Failed to fetch countries:', err));

    fetch(getBackendUrl() + '/api/roles')
      .then(res => res.json())
      .then(data => setRoles(data.roles || []))
      .catch(err => console.error('Failed to fetch roles:', err));

    // Fetch job categories with fallback (now also includes groups)
    fetch(getBackendUrl() + '/api/job-categories')
      .then(res => res.json())
      .then(data => {
        const cats = data.categories || [];
        const grps = data.groups || [];
        setJobCategories(cats.length > 0 ? cats : FALLBACK_CATEGORIES);
        setJobCategoryGroups(grps.length > 0 ? grps : FALLBACK_GROUPS);
        setCategoriesLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch job categories, using fallback:', err);
        setJobCategories(FALLBACK_CATEGORIES);
        setJobCategoryGroups(FALLBACK_GROUPS);
        setCategoriesLoading(false);
      });

    // Fetch branding
    fetch(getBackendUrl() + '/api/branding')
      .then(res => res.json())
      .then(data => {
        if (data.logo_light_url) {
          setLogoUrl(data.logo_light_url);
        }
        if (data.platform_name) {
          setPlatformName(data.platform_name);
        }
      })
      .catch(err => console.error('Failed to fetch branding:', err));
  }, []);

  // Auto-detect currency when country changes
  useEffect(() => {
    if (formData.country) {
      // Currency map uses lowercase country codes (matching API response)
      const currencyMap = {
        'madagascar': 'MGA',
        'benin': 'XOF',
        'senegal': 'XOF',
        'cameroon': 'XAF',
        'morocco': 'MAD',
        'tunisia': 'TND',
        'algeria': 'DZD',
        'mauritius': 'MUR',
        'france': 'EUR',
        'belgium': 'EUR',
        'switzerland': 'CHF',
        'canada': 'CAD',
      };
      
      // Use lowercase for lookup to match country codes from API
      const detectedCurrency = currencyMap[formData.country.toLowerCase()] || 'EUR';
      setFormData(prev => ({ ...prev, currency: detectedCurrency }));
    }
  }, [formData.country]);

  // Human-readable labels for error messages (fallback uses key)
  const FIELD_LABELS = {
    first_name: 'Prénom',
    last_name: 'Nom',
    email: 'Email',
    phone: 'Téléphone',
    country: 'Pays',
    city: 'Ville',
    job_categories: 'Catégorie(s) de métier',
    other_category_label: 'Précisez votre métier (Autre)',
    main_profession: 'Intitulé de poste (optionnel)',
    years_of_experience: "Années d'expérience",
    availability: 'Disponibilité',
    french_level: 'Niveau de français',
    expected_salary_local: 'Prétention salariale',
    currency: 'Devise',
    cv_file: 'CV',
    referral_source: "Comment nous avez-vous connu ?",
    consent_data_accuracy: "Certification d'exactitude",
    consent_data_storage: "Consentement au stockage des données",
    consent_data_sharing: "Consentement au partage avec les recruteurs",
  };

  // Order used to scroll/focus the FIRST missing field (top to bottom of the form)
  const FIELD_ORDER = [
    'first_name', 'last_name', 'email', 'phone', 'country', 'city',
    'job_categories', 'other_category_label', 'main_profession', 'years_of_experience', 'availability',
    'french_level', 'expected_salary_local', 'currency',
    'cv_file', 'referral_source',
    'consent_data_accuracy', 'consent_data_storage', 'consent_data_sharing',
  ];

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());

  const validateForm = () => {
    const errs = {};
    const trim = (v) => (v || '').toString().trim();

    if (!trim(formData.first_name)) errs.first_name = 'Prénom obligatoire.';
    if (!trim(formData.last_name)) errs.last_name = 'Nom obligatoire.';
    if (!trim(formData.email)) errs.email = 'Email obligatoire.';
    else if (!isValidEmail(formData.email)) errs.email = "Format d'email invalide (ex. nom@domaine.com).";
    if (!trim(formData.phone)) errs.phone = 'Téléphone obligatoire.';
    if (!trim(formData.country)) errs.country = 'Veuillez sélectionner votre pays.';
    if (!trim(formData.city)) errs.city = 'Ville obligatoire.';
    if (!formData.job_categories || formData.job_categories.length === 0) errs.job_categories = 'Sélectionnez au moins une catégorie de métier.';
    // If "Autre" is among selected categories → require a free-text description so the admin can re-classify
    if ((formData.job_categories || []).includes('autre') && !trim(formData.other_category_label)) {
      errs.other_category_label = "Précisez votre métier exact (puisque vous avez sélectionné « Autre »).";
    }
    // main_profession is now OPTIONAL — categories already capture the role
    if (formData.years_of_experience === null || formData.years_of_experience === undefined || formData.years_of_experience === '') {
      errs.years_of_experience = "Années d'expérience obligatoires.";
    }
    const salary = parseFloat(formData.expected_salary_local);
    if (!formData.expected_salary_local || isNaN(salary) || salary <= 0) {
      errs.expected_salary_local = 'Prétention salariale obligatoire (montant > 0).';
    }
    if (!trim(formData.currency)) {
      errs.currency = 'Devise manquante. Merci de sélectionner votre pays.';
    }
    if (!formData.cv_file) errs.cv_file = 'Veuillez télécharger votre CV (PDF, DOC ou DOCX).';
    if (!trim(formData.referral_source)) errs.referral_source = "Indiquez comment vous nous avez connus.";
    if (!formData.consent_data_accuracy) errs.consent_data_accuracy = "Veuillez certifier l'exactitude des informations fournies.";
    if (!formData.consent_data_storage) errs.consent_data_storage = "Veuillez accepter le stockage confidentiel de vos données.";
    if (!formData.consent_data_sharing) errs.consent_data_sharing = "Veuillez accepter le partage de vos données avec les recruteurs intéressés.";

    return errs;
  };

  const scrollToField = (field) => {
    // Special cases: job_categories is a custom section
    if (field === 'job_categories' && categoriesRef.current) {
      categoriesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const el = document.getElementById(field);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try {
        el.focus({ preventScroll: true });
      } catch (_) {}
    }
  };

  const firstErrorField = (errs) => FIELD_ORDER.find((k) => errs[k]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    // 1. Frontend pre-validation — catch every required field BEFORE network call
    const errs = validateForm();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      const missing = Object.keys(errs)
        .slice(0, 3)
        .map((k) => FIELD_LABELS[k] || k)
        .join(' · ');
      const extra = Object.keys(errs).length > 3 ? ` (+${Object.keys(errs).length - 3} autres)` : '';
      toast.error(`Merci de compléter : ${missing}${extra}`, { duration: 6000 });
      const first = firstErrorField(errs);
      if (first) setTimeout(() => scrollToField(first), 50);
      return;
    }

    // 2. Salary: show non-blocking warnings only. Market range is informative,
    //    never contractual — let the admin judge the final fit.

    // 3. All good frontend-side — clear errors
    setFieldErrors({});
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      const CONSENT_KEYS = ['consent_data_accuracy', 'consent_data_storage', 'consent_data_sharing', 'has_computer', 'has_internet'];
      Object.keys(formData).forEach(key => {
        const val = formData[key];
        if (val === null || val === undefined) return;
        if (val === '' && !CONSENT_KEYS.includes(key)) return;

        if (key === 'job_categories' && Array.isArray(val)) {
          formDataToSend.append('job_categories', val.join(','));
        } else if (typeof val === 'boolean') {
          formDataToSend.append(key, val.toString());
        } else {
          formDataToSend.append(key, val);
        }
      });
      formDataToSend.append('source_domain', window.location.origin);
      formDataToSend.append('consent_text_hash', 'v1-2026-04-23');

      const response = await fetch(getBackendUrl() + '/api/apply', {
        method: 'POST',
        body: formDataToSend,
      });

      // 4. Parse response
      let data = null;
      try {
        data = await response.json();
      } catch (_) {
        data = null;
      }

      // 4a. Backend validation error (422) — map detail[].loc[1] → fieldErrors
      if (response.status === 422 && data?.detail && Array.isArray(data.detail)) {
        const backendErrs = {};
        data.detail.forEach((d) => {
          const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : null;
          if (!field) return;
          let msg = d.msg || 'Champ invalide.';
          // Pretty common msgs
          if (d.type === 'missing') msg = `${FIELD_LABELS[field] || field} : champ obligatoire.`;
          else if (d.type === 'greater_than') msg = `${FIELD_LABELS[field] || field} : valeur doit être supérieure à ${d.ctx?.gt ?? 0}.`;
          else if (d.type === 'string_too_short') msg = `${FIELD_LABELS[field] || field} : trop court.`;
          backendErrs[field] = msg;
        });
        setFieldErrors(backendErrs);
        const first = firstErrorField(backendErrs) || Object.keys(backendErrs)[0];
        if (first) setTimeout(() => scrollToField(first), 50);
        toast.error("Le serveur a refusé certaines données. Corrigez les champs en rouge et renvoyez.", { duration: 7000 });
        return;
      }

      // 4b. Other non-2xx
      if (!response.ok) {
        const errMsg = data?.detail || data?.message || `Erreur serveur (HTTP ${response.status}). Merci de réessayer dans quelques instants.`;
        toast.error(typeof errMsg === 'string' ? errMsg : "Erreur serveur inattendue.", { duration: 7000 });
        return;
      }

      // 4c. Success
      if (data?.success) {
        setQualificationLink(data.qualification_link);
        setSubmitted(true);
        window.scrollTo(0, 0);
      } else {
        // Backend responded 200 but success=false (e.g. auto-rejection)
        toast.error(data?.message || "Votre candidature n'a pas pu être enregistrée.", { duration: 8000 });
      }
    } catch (error) {
      console.error('Application submit error:', error);
      // Network / timeout
      toast.error("Connexion au serveur impossible. Vérifiez votre connexion Internet et réessayez.", { duration: 8000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-tp-bg">
      {/* SEO Meta Tags */}
      <SeoHead pageTitle="Postuler | Talent Partner" />
      
      {/* Candidate Portal Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-tp-border" data-testid="candidate-navigation">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center gap-2 text-sm text-tp-text-light hover:text-tp-primary transition-colors" data-testid="back-to-client-site">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Retour au site</span>
            </Link>
            
            <div className="flex items-center" data-testid="candidate-logo">
              {logoUrl ? (
                <img 
                  src={resolveAssetUrl(logoUrl)}
                  alt={platformName}
                  className="h-12 sm:h-14 max-w-[240px] object-contain"
                />
              ) : (
                <span className="text-lg font-display font-semibold text-tp-primary">
                  {platformName}
                </span>
              )}
            </div>
            
            <div className="w-[100px]"></div>
          </div>
        </div>
      </nav>
      
      <section className="flex-1 py-12 md:py-16" data-testid="apply-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* SUCCESS STATE */}
          {submitted ? (
            <Card className="p-6 sm:p-10 text-center border-tp-border" data-testid="thank-you-card">
              <div className="w-16 h-16 bg-[#4C8F6E]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-8 w-8 text-[#4C8F6E]" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-display font-semibold text-tp-text mb-3">
                Merci {formData.first_name} !
              </h1>
              <p className="text-tp-text-light mb-8">
                Votre candidature a bien été envoyée. Notre équipe va analyser votre profil.
              </p>

              {qualificationLink && (
                <div className="bg-[#0F2744] text-white rounded-xl p-6 mb-8 text-left" data-testid="qualification-cta">
                  <div className="flex items-start gap-3 mb-4">
                    <ClipboardList className="h-6 w-6 text-[#4C8F6E] flex-shrink-0 mt-0.5" />
                    <div>
                      <h2 className="font-semibold text-lg mb-1">Complétez votre évaluation</h2>
                      <p className="text-white/70 text-sm">
                        Pour accélérer le traitement de votre dossier, complétez dès maintenant ce court formulaire (10-15 min).
                      </p>
                    </div>
                  </div>
                  <a href={qualificationLink} className="block">
                    <Button className="w-full bg-[#4C8F6E] hover:bg-[#3d7a5c] text-white h-12 text-base" data-testid="start-evaluation-btn">
                      Commencer l'évaluation <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </a>
                </div>
              )}

              <div className="space-y-4 text-left">
                <h3 className="font-semibold text-tp-text text-sm">Prochaines étapes :</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#4C8F6E]/10 text-[#4C8F6E] text-xs font-bold flex items-center justify-center">1</span>
                    <p className="text-sm text-tp-text-light">Analyse de votre CV par notre équipe</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#4C8F6E]/10 text-[#4C8F6E] text-xs font-bold flex items-center justify-center">2</span>
                    <p className="text-sm text-tp-text-light">Complétez le formulaire d'évaluation{qualificationLink ? ' (lien ci-dessus)' : ' (lien envoyé par email)'}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#4C8F6E]/10 text-[#4C8F6E] text-xs font-bold flex items-center justify-center">3</span>
                    <p className="text-sm text-tp-text-light">Entretien vidéo si votre profil correspond</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-tp-border flex items-center justify-center gap-2 text-sm text-tp-text-light">
                <Mail className="h-4 w-4" />
                <span>Un email de confirmation a été envoyé</span>
              </div>

              <Link to="/" className="block mt-6">
                <Button variant="outline" className="border-tp-border text-tp-text-light">
                  Retour à l'accueil
                </Button>
              </Link>
            </Card>
          ) : (
          <>
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-display font-semibold mb-4 text-tp-text">Postuler pour un emploi remote</h1>
            <p className="text-tp-text-light">Rejoignez notre réseau de professionnels francophones qualifiés</p>
          </div>

          {/* French Language Knockout Question */}
          {speaksFrench === null && (
            <Card className="p-8 border-tp-border" data-testid="french-knockout-card">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-display font-semibold mb-4 text-tp-text">Parlez-vous français couramment ?</h2>
                <p className="text-sm text-tp-text-light">Cette compétence est requise pour postuler</p>
              </div>

              <div className="flex gap-4 justify-center">
                <Button
                  size="lg"
                  onClick={() => setSpeaksFrench(true)}
                  className="bg-tp-accent hover:bg-tp-accent-light text-white px-8"
                  data-testid="french-yes-button"
                >
                  Oui
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => setSpeaksFrench(false)}
                  className="px-8 border-tp-border text-tp-text hover:bg-tp-bg"
                  data-testid="french-no-button"
                >
                  Non
                </Button>
              </div>
            </Card>
          )}

          {/* Rejection Message for Non-French Speakers */}
          {speaksFrench === false && (
            <Card className="p-12 text-center border-tp-border" data-testid="french-rejection-card">
              <div className="max-w-md mx-auto">
                <h2 className="text-2xl font-display font-semibold mb-6 text-tp-text">Merci pour votre intérêt.</h2>
                <p className="text-tp-text-light mb-4 leading-relaxed">
                  Nous recrutons uniquement des collaborateurs parlant français couramment.
                </p>
                <p className="text-tp-text-light mb-8 leading-relaxed">
                  Votre candidature ne correspond pas à nos critères actuels.
                </p>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="border-tp-border text-tp-primary hover:bg-tp-bg"
                  data-testid="return-home-button"
                >
                  Retour à l'accueil
                </Button>
              </div>
            </Card>
          )}

          {/* Application Form - Only shown if French = Yes */}
          {speaksFrench === true && formData.remote_compatible === null && (
            <Card className="p-6 sm:p-8 border-tp-border" data-testid="remote-gate">
              <h2 className="text-xl sm:text-2xl font-display text-tp-primary mb-3">
                Une dernière question avant de commencer
              </h2>
              <p className="text-sm text-tp-text mb-6">
                Talent Partner se concentre uniquement sur des postes <strong>100% télétravail</strong>.
                Avant d'aller plus loin, votre travail peut-il être réalisé entièrement à distance,
                depuis chez vous, sans présence physique requise ?
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  type="button"
                  onClick={() => setFormData({ ...formData, remote_compatible: true })}
                  className="bg-tp-primary hover:bg-tp-primary/90 text-white h-12 text-base"
                  data-testid="remote-gate-yes"
                >
                  ✅ Oui, 100% à distance
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFormData({ ...formData, remote_compatible: false })}
                  className="border-tp-border h-12 text-base"
                  data-testid="remote-gate-no"
                >
                  ❌ Non, présence physique nécessaire
                </Button>
              </div>
            </Card>
          )}

          {speaksFrench === true && formData.remote_compatible === false && (
            <Card className="p-6 sm:p-8 border-tp-border bg-orange-50/30" data-testid="remote-gate-rejected">
              <h2 className="text-xl sm:text-2xl font-display text-tp-primary mb-3">
                Désolé, votre profil ne correspond pas
              </h2>
              <p className="text-sm text-tp-text mb-3">
                Talent Partner accompagne uniquement des entreprises qui recrutent en <strong>télétravail complet</strong>.
                Si votre métier nécessite une présence physique (terrain, atelier, contact direct avec public), nous ne pouvons malheureusement pas vous proposer de mission.
              </p>
              <p className="text-xs text-tp-text-light">
                Si vous pensez avoir mal compris la question, vous pouvez revenir en arrière.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => setFormData({ ...formData, remote_compatible: null })}
                className="mt-4"
                data-testid="remote-gate-back"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Revenir
              </Button>
            </Card>
          )}

          {speaksFrench === true && formData.remote_compatible === true && (
            <Card className="p-8 border-tp-border">
              {/* Funnel: form_started fires once when user reaches the actual form */}
              <FunnelBeacon event="form_started" source="/postuler/formulaire" />
              <form onSubmit={handleSubmit} noValidate className="space-y-6">
              {/* Global error summary — appears after a failed submit */}
              {Object.keys(fieldErrors).length > 0 && (
                <div
                  className="rounded-md border border-red-300 bg-red-50 p-3 sm:p-4"
                  role="alert"
                  data-testid="form-error-summary"
                >
                  <p className="text-sm font-semibold text-red-700 mb-1">
                    Impossible d'envoyer la candidature. Merci de corriger :
                  </p>
                  <ul className="list-disc pl-5 text-xs text-red-700 space-y-0.5">
                    {Object.entries(fieldErrors).slice(0, 6).map(([field, msg]) => (
                      <li key={field}>
                        <button
                          type="button"
                          onClick={() => scrollToField(field)}
                          className="underline hover:no-underline text-left"
                          data-testid={`error-jump-${field}`}
                        >
                          {msg}
                        </button>
                      </li>
                    ))}
                    {Object.keys(fieldErrors).length > 6 && (
                      <li>… et {Object.keys(fieldErrors).length - 6} autre(s) champ(s) en rouge.</li>
                    )}
                  </ul>
                </div>
              )}

              <PersonalInfoSection formData={formData} setFormData={setFormData} countries={countries} fieldErrors={fieldErrors} />


              {/* Professional Information */}
              <div>
                <h3 className="text-lg font-display font-semibold mb-4 text-tp-text">Informations professionnelles</h3>
                <div className="space-y-4">
                  {/* Job Categories Multi-Select */}
                  <div ref={categoriesRef} data-testid="categories-section">
                    <Label className="text-tp-text">Catégorie(s) de métier * <span className="text-xs text-tp-text-light">(sélectionnez 1 à 3 catégories)</span></Label>
                    {categoriesLoading ? (
                      <div className="mt-2 p-4 bg-gray-50 rounded-lg text-center">
                        <Loader2 className="h-5 w-5 animate-spin mx-auto text-tp-text-light mb-2" />
                        <p className="text-sm text-tp-text-light">Chargement des catégories...</p>
                      </div>
                    ) : (
                      <div className={`mt-2 p-3 rounded-lg border-2 transition-colors ${
                        hasAttemptedSubmit && formData.job_categories.length === 0 
                          ? 'border-red-400 bg-red-50/50' 
                          : formData.job_categories.length > 0 
                            ? 'border-[#4C8F6E]/30 bg-[#4C8F6E]/5' 
                            : 'border-transparent'
                      }`}>
                        {/* Selected categories visible at top — keeps user oriented when groups are collapsed */}
                        {formData.job_categories.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3 p-2 bg-white rounded-md border border-tp-primary/20" data-testid="selected-categories-summary">
                            <span className="text-xs text-tp-text-light self-center mr-1">Sélectionné{formData.job_categories.length > 1 ? 's' : ''} :</span>
                            {formData.job_categories.map(id => {
                              const cat = jobCategories.find(c => c.id === id);
                              if (!cat) return null;
                              return (
                                <button
                                  key={id}
                                  type="button"
                                  onClick={() => setFormData({
                                    ...formData,
                                    job_categories: formData.job_categories.filter(x => x !== id),
                                  })}
                                  className="inline-flex items-center gap-1 text-xs bg-tp-primary text-white rounded-full px-2.5 py-1 hover:bg-tp-primary/90 transition-colors"
                                  data-testid={`selected-chip-${id}`}
                                  aria-label={`Retirer ${cat.label_fr}`}
                                >
                                  {cat.label_fr}
                                  <span aria-hidden="true">×</span>
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* Search bar */}
                        <div className="relative mb-3">
                          <Input
                            type="search"
                            value={categorySearch}
                            onChange={(e) => setCategorySearch(e.target.value)}
                            placeholder="Rechercher un métier… (ex. closer, ads, no-code)"
                            className="pl-9 h-10 text-sm"
                            data-testid="input-category-search"
                            aria-label="Rechercher un métier"
                          />
                          <svg
                            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-tp-text-light pointer-events-none"
                            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          >
                            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
                          </svg>
                        </div>

                        {/* Drill-down accordion: collapsed group cards by default,
                            expand on click. Search auto-expands matching groups. */}
                        {(() => {
                          const search = categorySearch.trim().toLowerCase();
                          const isSearching = search !== '';
                          const groupsOrdered = [...(jobCategoryGroups || [])].sort((a, b) => (a.order || 99) - (b.order || 99));
                          let totalVisible = 0;

                          const sections = groupsOrdered.map(group => {
                            const allCats = jobCategories.filter(c => (c.group || 'autre') === group.id);
                            const matchingCats = isSearching
                              ? allCats.filter(c => (c.label_fr || '').toLowerCase().includes(search))
                              : allCats;
                            if (allCats.length === 0) return null;
                            // When searching, only show groups with matches
                            if (isSearching && matchingCats.length === 0) return null;
                            totalVisible += matchingCats.length;

                            const selectedInGroup = allCats.filter(c => formData.job_categories.includes(c.id)).length;
                            // Open the panel if: user clicked it, search is active, OR a category from this group is selected
                            const isOpen = isSearching || expandedGroup === group.id || selectedInGroup > 0;

                            return (
                              <div
                                key={group.id}
                                className={`mb-2 rounded-lg border transition-colors ${isOpen ? 'border-tp-primary/30 bg-white' : 'border-tp-border bg-white hover:border-tp-primary/40'}`}
                                data-testid={`category-group-${group.id}`}
                              >
                                {/* Group card header — always visible, clickable to toggle */}
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (isSearching) return;  // search drives expansion when active
                                    setExpandedGroup(isOpen && expandedGroup === group.id ? null : group.id);
                                  }}
                                  className="w-full flex items-center justify-between p-3 sm:p-4 text-left hover:bg-tp-primary/5 rounded-lg transition-colors"
                                  aria-expanded={isOpen}
                                  data-testid={`category-group-header-${group.id}`}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-sm sm:text-base font-semibold text-tp-text">
                                      {group.label_fr}
                                    </span>
                                    <span className="text-[11px] text-tp-text-light bg-tp-bg rounded-full px-2 py-0.5">
                                      {allCats.length} métier{allCats.length > 1 ? 's' : ''}
                                    </span>
                                    {selectedInGroup > 0 && (
                                      <span className="text-[11px] text-white bg-tp-primary rounded-full px-2 py-0.5 font-semibold">
                                        {selectedInGroup} sélectionné{selectedInGroup > 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </div>
                                  <span
                                    className={`text-tp-text-light text-lg transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                                    aria-hidden="true"
                                  >
                                    ▾
                                  </span>
                                </button>

                                {/* Sub-categories — shown when group is open */}
                                {isOpen && (
                                  <div className="px-3 pb-3 sm:px-4 sm:pb-4">
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 pt-1 border-t border-tp-border/50 -mt-1">
                                      {matchingCats.map(cat => {
                                        const isSelected = formData.job_categories.includes(cat.id);
                                        const isDisabled = !isSelected && formData.job_categories.length >= 3;
                                        return (
                                          <button
                                            key={cat.id}
                                            type="button"
                                            disabled={isDisabled}
                                            onClick={() => {
                                              if (isSelected) {
                                                setFormData({
                                                  ...formData,
                                                  job_categories: formData.job_categories.filter(id => id !== cat.id),
                                                });
                                              } else if (formData.job_categories.length < 3) {
                                                setFormData({
                                                  ...formData,
                                                  job_categories: [...formData.job_categories, cat.id],
                                                });
                                              }
                                            }}
                                            title={cat.description || cat.label_fr}
                                            className={`
                                              mt-2 relative p-2.5 rounded-lg border text-left transition-all text-xs sm:text-sm
                                              ${isSelected
                                                ? 'border-tp-primary bg-tp-primary/10 text-tp-primary font-semibold'
                                                : isDisabled
                                                  ? 'border-tp-border bg-gray-50 text-gray-400 cursor-not-allowed'
                                                  : 'border-tp-border bg-white text-tp-text hover:border-tp-primary/50'
                                              }
                                            `}
                                            data-testid={`category-${cat.id}`}
                                          >
                                            <div className="flex items-center gap-1.5">
                                              {isSelected && <CheckCircle className="h-3.5 w-3.5 text-tp-primary flex-shrink-0" />}
                                              <span className="font-medium leading-tight">{cat.label_fr}</span>
                                            </div>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          });

                          if (isSearching && totalVisible === 0) {
                            return (
                              <div className="text-center py-6 text-sm text-tp-text-light" data-testid="category-no-results">
                                Aucun métier ne correspond à « {categorySearch} ».
                                <br />
                                <button
                                  type="button"
                                  onClick={() => setCategorySearch('')}
                                  className="text-tp-primary underline mt-1 inline-block"
                                >
                                  Effacer la recherche
                                </button>{' '}
                                ou ouvrez le groupe <strong>Autre</strong> pour préciser votre métier.
                              </div>
                            );
                          }

                          return sections;
                        })()}
                      </div>
                    )}
                    {hasAttemptedSubmit && formData.job_categories.length === 0 && (
                      <p className="text-sm text-red-600 font-medium mt-2 p-2 bg-red-50 rounded border border-red-200">
                        Veuillez sélectionner au moins une catégorie ci-dessus
                      </p>
                    )}
                    {formData.job_categories.length > 0 && (
                      <p className="text-xs text-tp-text-light mt-2">
                        La première catégorie sélectionnée ({jobCategories.find(c => c.id === formData.job_categories[0])?.label_fr || formData.job_categories[0]}) sera votre catégorie principale.
                      </p>
                    )}

                    {/* Mandatory free-text when "Autre" is picked — admin needs context to re-classify */}
                    {formData.job_categories.includes('autre') && (
                      <div className="mt-4 p-3 rounded-lg border border-orange-300 bg-orange-50/40" data-testid="other-category-block">
                        <Label htmlFor="other_category_label" className="text-tp-text text-sm font-semibold">
                          Précisez votre métier exact *
                        </Label>
                        <p className="text-xs text-tp-text-light mt-0.5 mb-2">
                          Vous avez choisi « Autre ». Indiquez votre intitulé exact pour qu'on puisse vous orienter (ex. <em>Voice Over Artist</em>, <em>Data Analyst</em>, <em>Traducteur EN→FR</em>).
                        </p>
                        <Input
                          id="other_category_label"
                          required
                          value={formData.other_category_label}
                          onChange={(e) => setFormData({ ...formData, other_category_label: e.target.value })}
                          placeholder="Votre métier précis"
                          aria-invalid={!!fieldErrors.other_category_label}
                          className={fieldErrors.other_category_label ? 'border-red-500 focus-visible:ring-red-400' : ''}
                          data-testid="input-other-category-label"
                        />
                        {fieldErrors.other_category_label && (
                          <p className="text-xs text-red-600 mt-1 flex items-start gap-1" data-testid="error-other_category_label">
                            <span>⚠️</span><span>{fieldErrors.other_category_label}</span>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="main_profession" className="text-tp-text">
                      Intitulé de poste{' '}
                      <span className="text-xs font-normal text-tp-text-light">(optionnel)</span>
                    </Label>
                    <p className="text-xs text-tp-text-light mt-0.5 mb-1.5">
                      Si vous avez un intitulé précis (ex. <em>Senior Media Buyer</em>, <em>Lead Closer EN/FR</em>), indiquez-le. Sinon, laissez vide.
                    </p>
                    <select
                      id="main_profession"
                      value={formData.main_profession}
                      onChange={(e) => setFormData({ ...formData, main_profession: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="select-profession"
                    >
                      <option value="">— Aucun intitulé précis —</option>
                      {roles.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="secondary_skills" className="text-tp-text">Compétences secondaires</Label>
                    <Input
                      id="secondary_skills"
                      placeholder="Ex: Meta Ads, Création de contenu, Excel avancé..."
                      value={formData.secondary_skills}
                      onChange={(e) => setFormData({ ...formData, secondary_skills: e.target.value })}
                      data-testid="input-secondary-skills"
                    />
                    <p className="text-xs text-tp-text-light mt-1">Séparez par des virgules</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="years_of_experience" className="text-tp-text">Années d'expérience *</Label>
                      <Input
                        id="years_of_experience"
                        type="number"
                        min="0"
                        required
                        value={formData.years_of_experience}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const parsed = raw === '' ? '' : parseInt(raw);
                          setFormData({ ...formData, years_of_experience: Number.isNaN(parsed) ? '' : parsed });
                        }}
                        className={fieldErrors.years_of_experience ? 'border-red-500 focus-visible:ring-red-400' : ''}
                        aria-invalid={!!fieldErrors.years_of_experience}
                        data-testid="input-experience"
                      />
                      {fieldErrors.years_of_experience && (
                        <p className="text-xs text-red-600 mt-1 flex items-start gap-1" data-testid="error-years_of_experience">
                          <span>⚠️</span><span>{fieldErrors.years_of_experience}</span>
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="availability" className="text-tp-text">Disponibilité *</Label>
                      <select
                        id="availability"
                        required
                        value={formData.availability}
                        onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        data-testid="select-availability"
                      >
                        <option value="full-time">Temps plein</option>
                        <option value="part-time">Temps partiel</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Contract Type: Full or Partial */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contract_type" className="text-tp-text">Type de contrat *</Label>
                      <select
                        id="contract_type"
                        required
                        value={formData.contract_type}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData({ 
                            ...formData, 
                            contract_type: value,
                            partial_percentage: value === 'full' ? null : (formData.partial_percentage || 50)
                          });
                        }}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        data-testid="select-contract-type"
                      >
                        <option value="full">Temps complet (100%)</option>
                        <option value="partial">Temps partiel</option>
                      </select>
                    </div>
                    
                    {formData.contract_type === 'partial' && (
                      <div>
                        <Label htmlFor="partial_percentage" className="text-tp-text">Pourcentage du temps *</Label>
                        <select
                          id="partial_percentage"
                          required
                          value={formData.partial_percentage?.toString() || '50'}
                          onChange={(e) => setFormData({ ...formData, partial_percentage: parseInt(e.target.value) })}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          data-testid="select-partial-percentage"
                        >
                          <option value="20">20%</option>
                          <option value="30">30%</option>
                          <option value="40">40%</option>
                          <option value="50">50%</option>
                          <option value="60">60%</option>
                          <option value="70">70%</option>
                          <option value="80">80%</option>
                          <option value="90">90%</option>
                        </select>
                        <p className="text-xs text-tp-text-light mt-1">
                          La fourchette indicative du marché est automatiquement ajustée à ce pourcentage.
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="french_level" className="text-tp-text">Niveau de français *</Label>
                    <select
                      id="french_level"
                      required
                      value={formData.french_level}
                      onChange={(e) => setFormData({ ...formData, french_level: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="select-french"
                    >
                      <option value="native">Langue maternelle</option>
                      <option value="fluent">Courant</option>
                      <option value="intermediate">Intermédiaire</option>
                      <option value="basic">Basique</option>
                    </select>
                  </div>
                  
                  {/* Salary Information */}
                  <div className="border-t border-tp-border pt-4 mt-4">
                    <h4 className="font-medium mb-4 text-tp-text">Prétentions salariales</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="expected_salary_local" className="text-tp-text">
                          Salaire mensuel souhaité {formData.contract_type === 'partial' && formData.partial_percentage
                            ? `(pour temps partiel ${formData.partial_percentage}%)`
                            : '(temps complet 100%)'
                          } *
                        </Label>
                        <Input
                          id="expected_salary_local"
                          type="number"
                          min="1"
                          step="1"
                          required
                          placeholder={
                            effectiveSalaryRange?.min && effectiveSalaryRange?.max
                              ? `Ex. ${Math.round((effectiveSalaryRange.min + effectiveSalaryRange.max) / 2).toLocaleString('fr-FR')} ${effectiveSalaryRange.currency || ''}/mois`
                              : 'Montant mensuel'
                          }
                          value={formData.expected_salary_local}
                          onChange={(e) => setFormData({ ...formData, expected_salary_local: e.target.value })}
                          className={
                            fieldErrors.expected_salary_local
                              ? 'border-red-500 bg-red-50/40 focus-visible:ring-red-400'
                              : (salaryWarning?.level === 'extreme-low' || salaryWarning?.level === 'extreme-high')
                                ? 'border-red-400 bg-red-50/40'
                                : salaryWarning?.level === 'soft'
                                  ? 'border-orange-300 bg-orange-50/30'
                                  : ''
                          }
                          aria-invalid={!!fieldErrors.expected_salary_local}
                          data-testid="input-salary-local"
                        />

                        {/* Persistent market range hint — only when we have RELIABLE data.
                            If the backend says data_available=false (niche category, country
                            without dataset, etc.) we display "Indisponible" rather than a
                            misleading number. Better than fake. */}
                        {effectiveSalaryRange?.min && effectiveSalaryRange?.max ? (
                          <p
                            className="text-xs mt-1 text-tp-primary bg-tp-primary/5 border border-tp-primary/20 rounded-md px-2 py-1.5 flex items-start gap-1.5"
                            data-testid="public-salary-suggestion"
                          >
                            <span className="mt-0.5">💡</span>
                            <span>
                              Fourchette indicative du marché pour ce profil
                              {effectiveSalaryRange.isPartTime ? ` (temps partiel ${effectiveSalaryRange.percentage}%)` : ''} :{' '}
                              <strong>
                                {effectiveSalaryRange.min.toLocaleString('fr-FR')}—{effectiveSalaryRange.max.toLocaleString('fr-FR')} {effectiveSalaryRange.currency || ''}/mois
                              </strong>
                              {' '}· tolérance indicative ±30% — vous pouvez postuler même hors fourchette.
                            </span>
                          </p>
                        ) : (formData.country && formData.job_categories.length > 0 && salaryRange && salaryRange.data_available === false) ? (
                          <p
                            className="text-xs mt-1 text-tp-text-light bg-tp-bg border border-tp-border rounded-md px-2 py-1.5 flex items-start gap-1.5"
                            data-testid="public-salary-unavailable"
                          >
                            <span className="mt-0.5">ℹ️</span>
                            <span>
                              Benchmark indisponible pour ce profil ({formData.country}). Indiquez le montant que vous estimez juste — l'équipe l'examinera avec vous.
                            </span>
                          </p>
                        ) : null}

                        {/* Error / warning messages below the persistent hint */}
                        {fieldErrors.expected_salary_local ? (
                          <p className="text-xs text-red-600 mt-1 flex items-start gap-1 font-medium" data-testid="error-expected_salary_local">
                            <span>⚠️</span><span>{fieldErrors.expected_salary_local}</span>
                          </p>
                        ) : salaryWarning ? (
                          <p
                            className={`text-xs mt-1 ${(salaryWarning.level === 'extreme-low' || salaryWarning.level === 'extreme-high') ? 'text-red-600 font-medium' : 'text-orange-600'}`}
                            data-testid="public-salary-warning"
                          >
                            ⚠️ {salaryWarning.text}
                          </p>
                        ) : !effectiveSalaryRange?.min && (
                          <p className="text-xs text-tp-text-light mt-1">
                            Montant mensuel dans votre devise locale (obligatoire). Complétez pays + catégorie + expérience pour voir la fourchette indicative.
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="currency" className="text-tp-text">Devise</Label>
                        <Input
                          id="currency"
                          type="text"
                          readOnly
                          value={formData.currency || 'Sélectionnez un pays'}
                          className="bg-gray-100 cursor-not-allowed"
                          data-testid="input-currency"
                        />
                        <p className="text-xs text-tp-text-light mt-1">Devise automatique selon votre pays</p>
                        {fieldErrors.currency && (
                          <p className="text-xs text-red-600 mt-1 flex items-start gap-1" data-testid="error-currency">
                            <span>⚠️</span><span>{fieldErrors.currency}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Requirements Check */}
              <div>
                <h3 className="text-lg font-display font-semibold mb-4 text-tp-text">Prérequis techniques *</h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.has_computer}
                      onChange={(e) => setFormData({ ...formData, has_computer: e.target.checked })}
                      className="rounded border-tp-border text-tp-accent focus:ring-tp-accent"
                      data-testid="checkbox-computer"
                    />
                    <span className="text-sm text-tp-text">J'ai un ordinateur personnel</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.has_internet}
                      onChange={(e) => setFormData({ ...formData, has_internet: e.target.checked })}
                      className="rounded border-tp-border text-tp-accent focus:ring-tp-accent"
                      data-testid="checkbox-internet"
                    />
                    <span className="text-sm text-tp-text">J'ai une connexion internet stable</span>
                  </label>
                </div>
              </div>

              <DocumentsSection formData={formData} setFormData={setFormData} fieldErrors={fieldErrors} />

              {/* RGPD Consent — required before submission */}
              <div className="rounded-md border border-tp-border bg-tp-bg/40 p-4 md:p-5 space-y-3" data-testid="rgpd-consent-block">
                <h3 className="text-sm font-semibold text-tp-text flex items-center gap-2">
                  <span aria-hidden>🔒</span> Consentement et charte de confidentialité
                </h3>
                <p className="text-xs text-tp-text-light">
                  Pour traiter votre candidature et vous mettre en relation avec nos clients,
                  merci de confirmer les 3 points suivants. Vos choix sont horodatés et conservés
                  comme preuve de consentement (RGPD).
                </p>

                {/* Consent 1 */}
                <div>
                  <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition ${fieldErrors.consent_data_accuracy ? 'border-red-400 bg-red-50/40' : 'border-tp-border hover:bg-white'}`}>
                    <input
                      type="checkbox"
                      checked={!!formData.consent_data_accuracy}
                      onChange={(e) => setFormData({ ...formData, consent_data_accuracy: e.target.checked })}
                      className="mt-0.5 h-4 w-4 accent-tp-accent"
                      data-testid="consent-accuracy"
                    />
                    <span className="text-xs sm:text-sm text-tp-text leading-relaxed">
                      Je certifie que <strong>les informations fournies sont exactes et vérifiables</strong>.
                    </span>
                  </label>
                  {fieldErrors.consent_data_accuracy && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-consent_data_accuracy">⚠️ {fieldErrors.consent_data_accuracy}</p>
                  )}
                </div>

                {/* Consent 2 */}
                <div>
                  <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition ${fieldErrors.consent_data_storage ? 'border-red-400 bg-red-50/40' : 'border-tp-border hover:bg-white'}`}>
                    <input
                      type="checkbox"
                      checked={!!formData.consent_data_storage}
                      onChange={(e) => setFormData({ ...formData, consent_data_storage: e.target.checked })}
                      className="mt-0.5 h-4 w-4 accent-tp-accent"
                      data-testid="consent-storage"
                    />
                    <span className="text-xs sm:text-sm text-tp-text leading-relaxed">
                      J'accepte que mes données soient <strong>stockées de manière confidentielle</strong>{' '}
                      et utilisées dans le cadre de ma mise en relation avec des entreprises intéressées.
                    </span>
                  </label>
                  {fieldErrors.consent_data_storage && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-consent_data_storage">⚠️ {fieldErrors.consent_data_storage}</p>
                  )}
                </div>

                {/* Consent 3 */}
                <div>
                  <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition ${fieldErrors.consent_data_sharing ? 'border-red-400 bg-red-50/40' : 'border-tp-border hover:bg-white'}`}>
                    <input
                      type="checkbox"
                      checked={!!formData.consent_data_sharing}
                      onChange={(e) => setFormData({ ...formData, consent_data_sharing: e.target.checked })}
                      className="mt-0.5 h-4 w-4 accent-tp-accent"
                      data-testid="consent-sharing"
                    />
                    <span className="text-xs sm:text-sm text-tp-text leading-relaxed">
                      Je donne mon <strong>accord explicite</strong> pour que mes informations soient{' '}
                      communiquées à des recruteurs et entreprises clientes potentiellement intéressés par mon profil.
                    </span>
                  </label>
                  {fieldErrors.consent_data_sharing && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-consent_data_sharing">⚠️ {fieldErrors.consent_data_sharing}</p>
                  )}
                </div>

                <p className="text-[10px] text-tp-text-light italic">
                  Vous pouvez à tout moment demander la suppression de vos données en nous contactant.
                </p>
              </div>

              <div>
                <Button
                  type="submit"
                  size="lg"
                  disabled={loading}
                  className="bg-tp-primary hover:bg-tp-primary-light text-white"
                  data-testid="button-submit-application"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    'Envoyer ma candidature'
                  )}
                </Button>
              </div>
            </form>
          </Card>
          )}
          </>
          )}
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="bg-white border-t border-tp-border py-6" data-testid="candidate-footer">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-tp-text-light">
            &copy; {new Date().getFullYear()} {platformName}. Tous droits réservés.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ApplyPage;
